
import React from "react";
import { useThemeMode } from "@/hooks/useThemeMode";
import { useThemeContext } from "@/hooks/useThemeContext";
import InputLabel from "./components/InputLabel";
import AnimatedInput from "./components/AnimatedInput";
import FocusUnderline from "./components/FocusUnderline";
import DescriptionText from "./components/DescriptionText";
import { useInputAnimation } from "./hooks/useInputAnimation";

interface InputDisplayProps {
  label?: string;
  inputValue: string;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  labelClassName?: string;
  inputClassName?: string;
  description?: string;
  descriptionClassName?: string;
  onMouseUp?: (e: React.MouseEvent) => void;
  onKeyUp?: (e: React.KeyboardEvent) => void;
  [key: string]: any;
}

const InputDisplay: React.FC<InputDisplayProps> = ({
  label,
  inputValue,
  handleInputChange,
  handleBlur,
  disabled = false,
  labelClassName = "",
  inputClassName = "",
  description,
  descriptionClassName = "text-xs text-muted-foreground mt-1",
  onMouseUp,
  onKeyUp,
  ...props
}) => {
  const { isDark } = useThemeMode();
  const { useGlow } = useThemeContext();
  
  const { isFocused, hasInteracted, handleFocus, handleBlurWithAnimation } = useInputAnimation({
    disabled
  });
  
  const wrappedHandleBlur = handleBlurWithAnimation(handleBlur);
  
  if (label) {
    return (
      <>
        <div className="flex items-center justify-between relative">
          <InputLabel 
            label={label} 
            isFocused={isFocused} 
            labelClassName={labelClassName} 
          />
          
          <AnimatedInput
            inputValue={inputValue}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={wrappedHandleBlur}
            isFocused={isFocused}
            hasInteracted={hasInteracted}
            disabled={disabled}
            isDark={isDark}
            useGlow={useGlow}
            className={inputClassName}
            onMouseUp={onMouseUp}
            onKeyUp={onKeyUp}
            {...props}
          />
          
          <FocusUnderline isActive={isFocused} isDark={isDark} />
        </div>
        
        {description && (
          <DescriptionText 
            description={description} 
            className={descriptionClassName} 
          />
        )}
      </>
    );
  }
  
  return (
    <>
      <div className="relative">
        <AnimatedInput
          inputValue={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={wrappedHandleBlur}
          isFocused={isFocused}
          hasInteracted={hasInteracted}
          disabled={disabled}
          isDark={isDark}
          useGlow={useGlow}
          className={inputClassName}
          onMouseUp={onMouseUp}
          onKeyUp={onKeyUp}
          {...props}
        />
        
        <FocusUnderline isActive={isFocused} isDark={isDark} />
      </div>
      
      {description && (
        <DescriptionText 
          description={description} 
          className={descriptionClassName} 
        />
      )}
    </>
  );
};

export default InputDisplay;
